import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  PRICE_LIST_CATEGORIES,
  PRICE_LIST_CATEGORY_LABELS,
  PRICE_LIST_PRICING_MODES,
  PRICE_LIST_PRICING_MODE_LABELS,
  PRICE_LIST_UNITS,
  PRICE_LIST_UNIT_LABELS,
  type ApplianceFeature,
  type PriceListCategory,
  type PriceListPricingMode,
  type PriceListUnit,
} from "@xop/dashboard-shared";
import { useEffect, useState, type JSX } from "react";

import { fetchApplianceFeatures } from "@crm-src/pages/licences/licences-api";

import type { FeatureWrite, ProductFeature } from "../products-api.js";
import { centsToDollarsInput, dollarsToCents } from "./priceInput.js";

/**
 * Add / edit one feature — what it is AND what it costs.
 *
 * Until 2026-09-13 these were two forms on two pages: a feature had a name and
 * a 4-digit code here, and its price lived on a separate price-list row that
 * had to be kept in step by hand. One form is the whole point of the merge.
 *
 * The form's one piece of real logic is that the price FIELD follows the
 * pricing mode: a fixed item asks for dollars, a percentage item asks for a
 * rate, and an included item asks for nothing. Showing all three at once was
 * the alternative, and it made "$0, 0%, included" a state an operator could
 * save without noticing which of the three the feature actually was.
 *
 * THE APPLIANCE FEATURE (2026-09-15). A licence file names the appliance's own
 * keys, and since the licence builder picks from THIS list, each feature has
 * to say which key it is — `licence_key`, chosen here from the appliance's
 * fixed list (`GET /api/licences/appliance-features`). Until it is set, the
 * feature is sold but cannot go on a licence, and the list says so per row.
 */

export interface FeatureDialogProps {
  open: boolean;
  /** The feature being edited, or `null` when adding a new one. */
  feature: ProductFeature | null;
  /** The product it belongs to — named in the title so a long list of dialogs
   * never leaves the operator wondering which product they are adding to. */
  productName: string;
  /** Server-side error to show above the fields (e.g. a duplicate part number). */
  error?: string | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (body: FeatureWrite) => void;
}

interface FormState {
  name: string;
  code: string;
  part_number: string;
  /** The appliance key, or "" for none. */
  licence_key: string;
  category: PriceListCategory;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  /** Dollars as typed — converted to cents only on save. */
  price: string;
  percent_rate: string;
  notes: string;
  active: boolean;
  sort_order: string;
}

const EMPTY: FormState = {
  name: "",
  code: "",
  part_number: "",
  licence_key: "",
  category: "value_added_service",
  unit: "port",
  pricing_mode: "fixed",
  price: "",
  percent_rate: "",
  notes: "",
  active: true,
  sort_order: "0",
};

function stateFor(feature: ProductFeature | null): FormState {
  if (!feature) return EMPTY;
  return {
    name: feature.name,
    code: feature.code ?? "",
    part_number: feature.part_number ?? "",
    licence_key: feature.licence_key ?? "",
    category: feature.category,
    unit: feature.unit,
    pricing_mode: feature.pricing_mode,
    price:
      feature.pricing_mode === "fixed" && feature.price_cents > 0
        ? centsToDollarsInput(feature.price_cents)
        : "",
    percent_rate: feature.percent_rate == null ? "" : String(feature.percent_rate),
    notes: feature.notes ?? "",
    active: feature.active,
    sort_order: String(feature.sort_order ?? 0),
  };
}

export function FeatureDialog({
  open,
  feature,
  productName,
  error,
  saving = false,
  onClose,
  onSave,
}: FeatureDialogProps): JSX.Element {
  const [form, setForm] = useState<FormState>(() => stateFor(feature));
  const [touched, setTouched] = useState(false);
  // The appliance's key list, asked for once per opening. A failure leaves the
  // picker empty with a note — the rest of the form must still save.
  const [applianceFeatures, setApplianceFeatures] = useState<ApplianceFeature[] | null>(null);
  const [applianceFeaturesFailed, setApplianceFeaturesFailed] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setApplianceFeaturesFailed(false);
    fetchApplianceFeatures()
      .then((rows) => {
        if (!cancelled) setApplianceFeatures(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setApplianceFeatures([]);
          setApplianceFeaturesFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Re-seed whenever the dialog is (re)opened for a different feature, so the
  // previous row's values never leak into the next edit.
  useEffect(() => {
    if (open) {
      setForm(stateFor(feature));
      setTouched(false);
    }
  }, [open, feature]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const nameError = touched && !form.name.trim();
  // A blank price field is allowed — it means "not priced yet", which the list
  // says out loud. Only a field with unparseable text in it is an error.
  const priceError =
    touched &&
    form.pricing_mode === "fixed" &&
    form.price.trim() !== "" &&
    dollarsToCents(form.price) === null;

  function handleSave(): void {
    setTouched(true);
    if (!form.name.trim()) return;
    const cents = form.price.trim() === "" ? 0 : dollarsToCents(form.price);
    if (form.pricing_mode === "fixed" && cents === null) return;

    onSave({
      name: form.name.trim(),
      code: form.code.trim(),
      part_number: form.part_number.trim(),
      licence_key: form.licence_key.trim() || null,
      category: form.category,
      unit: form.unit,
      pricing_mode: form.pricing_mode,
      price_cents: form.pricing_mode === "fixed" ? (cents ?? 0) : 0,
      percent_rate:
        form.pricing_mode === "percent" ? Number(form.percent_rate || 0) : null,
      notes: form.notes.trim(),
      active: form.active,
      sort_order: Number(form.sort_order || 0),
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {feature ? `Edit ${feature.name}` : `New feature in ${productName}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
            error={nameError}
            helperText={nameError ? "A name is required." : " "}
            size="small"
            fullWidth
          />

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { sm: "1fr 1fr 1fr" } }}>
            <TextField
              label="Part number"
              value={form.part_number}
              onChange={(e) => set("part_number", e.target.value)}
              placeholder="700-2341-001"
              helperText="Printed. Unique."
              size="small"
              fullWidth
            />
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => set("code", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
              placeholder="6025"
              helperText="4 digits, internal."
              size="small"
              fullWidth
            />
            <TextField
              label="Sort order"
              value={form.sort_order}
              onChange={(e) => set("sort_order", e.target.value.replace(/[^0-9]/g, ""))}
              helperText="Position in the list."
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { sm: "1fr 1fr" } }}>
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={(e) => set("category", e.target.value as PriceListCategory)}
              size="small"
              fullWidth
            >
              {PRICE_LIST_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {PRICE_LIST_CATEGORY_LABELS[c]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Unit"
              value={form.unit}
              onChange={(e) => set("unit", e.target.value as PriceListUnit)}
              size="small"
              fullWidth
            >
              {PRICE_LIST_UNITS.map((u) => (
                <MenuItem key={u} value={u}>
                  {PRICE_LIST_UNIT_LABELS[u]}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Autocomplete<ApplianceFeature, false, false, false>
            options={applianceFeatures ?? []}
            loading={applianceFeatures === null}
            value={
              (applianceFeatures ?? []).find((option) => option.key === form.licence_key) ??
              (form.licence_key ? { key: form.licence_key, description: "" } : null)
            }
            onChange={(_event, option) => set("licence_key", option?.key ?? "")}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            getOptionLabel={(option) => option.key}
            renderOption={(props, option) => (
              <li {...props} key={option.key}>
                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <Typography component="span" sx={{ fontSize: 14 }}>
                    {option.key}
                  </Typography>
                  {option.description && (
                    <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Appliance feature"
                placeholder="Not linked"
                helperText={
                  applianceFeaturesFailed
                    ? "Could not load the appliance's feature list — try again later."
                    : "The key the licence file names for this feature. Unlinked, it cannot go on a licence."
                }
              />
            )}
          />

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { sm: "1fr 1fr" } }}>
            <TextField
              select
              label="Pricing"
              value={form.pricing_mode}
              onChange={(e) => set("pricing_mode", e.target.value as PriceListPricingMode)}
              size="small"
              fullWidth
            >
              {PRICE_LIST_PRICING_MODES.map((m) => (
                <MenuItem key={m} value={m}>
                  {PRICE_LIST_PRICING_MODE_LABELS[m]}
                </MenuItem>
              ))}
            </TextField>

            {form.pricing_mode === "fixed" && (
              <TextField
                label="Price"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                error={priceError}
                helperText={priceError ? "Enter an amount, e.g. 166.00" : "Leave blank until it is priced."}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            )}
            {form.pricing_mode === "percent" && (
              <TextField
                label="Rate"
                value={form.percent_rate}
                onChange={(e) =>
                  set("percent_rate", e.target.value.replace(/[^0-9.]/g, ""))
                }
                helperText="Percentage of the order, e.g. 15"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            )}
          </Box>

          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
            helperText="Prerequisites, minimums, anything a quote should say about this feature."
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
              />
            }
            label={
              form.active
                ? "Available — offered in quote and invoice pickers"
                : "Retired — hidden from pickers, kept for existing documents"
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving…" : feature ? "Save changes" : "Add feature"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
